import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';

// CONSTANTS
import {
  ROUTE_SHOWS_AND_MOVIES_DISCOVER,
  ROUTE_SHOWS_AND_MOVIES_MOVIE,
  ROUTE_SHOWS_AND_MOVIES_SHOW,
} from '@/constants/routes';
import { contentTypes } from '@/constants/watchList';
import { appsTypesFilter } from '@/constants/app';

// UTILITY
import { getLink, routeEnhance } from '@/utility/routes';

// HOOKS
import useActions from '@/hooks/useActions';

// STORE
import { ShowsAndMoviesActions } from '@/store/actions';

// COMPONENTS
import SearchAutoComplete from '@/components/Form/SearchAutoComplete';

const typesFilters = [
  { text: 'showsAndMovies.filters.types.show', value: contentTypes.show },
  { text: 'showsAndMovies.filters.types.movie', value: contentTypes.movie },
];

const filterInitialParams = {
  genres: [],
  appsType: appsTypesFilter[2].id,
  apps: [],
  type: typesFilters.map(type => type.value),
};

function getLinkWithQueryParams(route, params = {}) {
  const search = stringify(params, {
    arrayFormat: 'comma',
    skipEmptyString: true,
  });

  return `${route}?${search}`;
}

const ContentSearch = ({ analyticsLocation, textFieldProps }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { searchData, search_last_page, searchPrevString, searchLoading } = useSelector(
    ({ showsAndMovies }) => ({
      searchData: showsAndMovies.search.data,
      search_last_page: showsAndMovies.search.meta.last_page,
      searchPrevString: showsAndMovies.search.string,
      searchLoading: showsAndMovies.loadings.search,
    }),
  );

  const [getShowsAndMoviesSearch, cleanShowsAndMoviesSearch] = useActions([
    params => ShowsAndMoviesActions.getShowsAndMoviesSearch.action({ params }),
    ShowsAndMoviesActions.cleanShowsAndMoviesSearch.action,
  ]);

  const debouncedSearch = useCallback(
    debounce((s = '') => getShowsAndMoviesSearch({ search: s.trim() }), 700),
    [],
  );

  const handleSearch = (event, value, reason) => {
    if (event?.keyCode === 13 && reason === 'reset') {
      return;
    }

    if (reason === 'input' || reason === 'clear') {
      setSearch(value);

      if (value) {
        debouncedSearch(value);
      } else {
        debouncedSearch.cancel();
        cleanShowsAndMoviesSearch();
      }
    }
  };

  const handleSelect = (event, value, reason, handleOpen) => {
    if (reason === 'clear') {
      setSearch('');
      return;
    }

    if (reason === 'select-option') {
      const type = value?.type || '';
      let routeEnhanced;
      const options = {
        state: {
          location: analyticsLocation,
        },
      };

      switch (type) {
        case 'show':
          routeEnhanced = routeEnhance(
            getLink(ROUTE_SHOWS_AND_MOVIES_SHOW, { show_id: value.slug || value.id }),
            options,
          );

          navigate(routeEnhanced);

          break;
        case 'movie':
          routeEnhanced = routeEnhance(
            getLink(ROUTE_SHOWS_AND_MOVIES_MOVIE, { movie_id: value.slug || value.id }),
            options,
          );

          navigate(routeEnhanced);

          break;

        case 'link':
          if (value.link) window.open(value.link, '_self');
          break;

        case 'show more':
          routeEnhanced = getLinkWithQueryParams(ROUTE_SHOWS_AND_MOVIES_DISCOVER, {
            ...filterInitialParams,
            search: searchPrevString,
          });

          navigate(routeEnhanced);

          break;

        case 'show more enter':
          routeEnhanced = getLinkWithQueryParams(ROUTE_SHOWS_AND_MOVIES_DISCOVER, {
            ...filterInitialParams,
            search: value.value,
          });

          navigate(routeEnhanced);

          break;

        default:
          break;
      }

      handleOpen(false);
    }
  };

  return (
    <SearchAutoComplete
      inputValue={search}
      freeSolo={!search}
      onInputChange={handleSearch}
      onChange={handleSelect}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          debouncedSearch.cancel();
          getShowsAndMoviesSearch({ search: e.target.value.trim() });
        }
      }}
      options={search ? searchData : []}
      loading={searchLoading}
      openOnFocus
      searchPrevString={searchPrevString}
      searchPlaceholder={t('contentSearch.placeholder')}
      wrapProps={{ className: 'search' }}
      TextFieldProps={{
        className: 'search__input',
        variant: 'outlined',
        size: 'medium',
        ...textFieldProps,
      }}
      showMore={search_last_page}
    />
  );
};

ContentSearch.defaultProps = {
  analyticsLocation: '',
  textFieldProps: {},
};

ContentSearch.propTypes = {
  analyticsLocation: PropTypes.string,
  textFieldProps: PropTypes.object,
};

export default React.memo(ContentSearch);
